/**
 * **Sharing** (`/settings/sharing`, FR-29, FR-30, FR-36; W28, FR-65, plan D86)
 * — a new link's defaults, changed on the page and saved by the bar: how many
 * days it works (1 to 30), and whether every link must carry a passcode.
 *
 * Below them, read only, **how a sent link leaves** the organisation (D27):
 * each channel `core-communication` reports — from the organisation's own mail
 * server or WhatsApp number, or FiveOX's in its name — shown only to someone
 * who may see shares (`share:can_view`). The channels themselves are connected
 * in the FiveOX console (FR-37); ProDoc holds no credential.
 */

import { MAX_LINK_DAYS, MIN_LINK_DAYS, SETTINGS, SHARE, sharingOf, useLoad, type ChannelState, type SettingsAnswer, type SharingSettings as LinkDefaults } from '@fiveox/prodoc-ui'
import { Input, Skeleton, SkeletonRegion, TONE_TEXT, useToast } from '@fiveox/ui'
import { Link2, Send } from 'lucide-react'

import { EditBar, EditPage, useLeaveGuard } from '../../components/edit'
import { SettingsSection, SwitchRow } from '../../components/settings-parts'
import { useSettingsHost } from './host'
import { AreaWaiting, SETTINGS_BACK, useSettingsArea } from './settings-data'

const FAMILY: Record<string, string> = { email: 'E-mail', whatsapp: 'WhatsApp' }

/** How one channel would send a link, in the organisation's words — its own, FiveOX's in its name, or not yet. */
export function channelLine(state: ChannelState, orgName: string): { text: string; tone: string } {
  if (state.active || state.sends_as === 'org') return { text: `From ${orgName}’s own ${state.channel === 'email' ? 'mail server' : 'WhatsApp number'}${state.provider ? ` (${state.provider})` : ''}.`, tone: TONE_TEXT.success }
  if (state.sends_as === 'platform') return { text: `Not connected — FiveOX sends it in ${orgName}’s name.`, tone: 'text-muted-foreground' }
  return { text: `Cannot send yet${state.reason ? ` (${state.reason.replace(/_/g, ' ')})` : ''}.`, tone: TONE_TEXT.warning }
}

/** The page's slice: the days as typed, and the passcode switch. */
interface SharingDraft {
  days: string
  passcode: boolean
}

/* The page's slice of the settings document, and the one key it sends — module-level, so the area's memo holds. */
const draftOf = (s: SettingsAnswer): SharingDraft => {
  const sharing = sharingOf(s)
  return { days: String(sharing.link_expiry_days), passcode: sharing.link_passcode_required }
}
const sharingOfDraft = (d: SharingDraft): LinkDefaults => ({ link_expiry_days: Number(d.days), link_passcode_required: d.passcode })
const sharingPatch = (d: SharingDraft) => ({ sharing: sharingOfDraft(d) })
const sameSharing = (a: SharingDraft, b: SharingDraft) => JSON.stringify(sharingOfDraft(a)) === JSON.stringify(sharingOfDraft(b))

export function SharingSettings() {
  const { client, can, orgName, consoleUrl } = useSettingsHost()
  const { toast } = useToast()
  const area = useSettingsArea({ from: draftOf, toPatch: sharingPatch, same: sameSharing })
  const guard = useLeaveGuard(area.dirty)
  const editable = can(SETTINGS, 'can_update')
  const seesShares = can(SHARE, 'can_view')
  const channels = useLoad(() => (seesShares ? client.shares.channels() : Promise.resolve([] as ChannelState[])), [client, seesShares])
  const s = area.settings.data
  const draft = area.draft

  const n = Number(draft?.days)
  const daysError = draft && (!Number.isInteger(n) || n < MIN_LINK_DAYS || n > MAX_LINK_DAYS) ? `A whole number of days, ${MIN_LINK_DAYS} to ${MAX_LINK_DAYS}.` : null
  const set = (next: Partial<SharingDraft>) => draft && area.setDraft({ ...draft, ...next })

  const save = () => void area.save().then((saved) => saved && toast('Settings saved.'))

  return (
    <EditPage back={SETTINGS_BACK} title="Sharing" description={`The defaults for a new share link, and how a sent link leaves ${orgName}.`}>
      {guard}
      {!s || !draft ? (
        <AreaWaiting settings={area.settings} label="Loading the sharing settings" heights={['h-48', 'h-32']} />
      ) : (
        <>
          <SettingsSection
            id="link"
            icon={<Link2 size={18} aria-hidden />}
            title="A new share link"
            description="What a person sharing a document or a folder starts from. Every link expires."
          >
            <div className="divide-border divide-y">
              <div className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_12rem] sm:items-start">
                <div className="min-w-0">
                  <p className="text-foreground text-sm font-medium">Days a new link works</p>
                  <p className="text-muted-foreground mt-0.5 text-sm">
                    The person sharing may choose a shorter or longer time for one link, from {MIN_LINK_DAYS} to {MAX_LINK_DAYS} days.
                  </p>
                </div>
                <Input
                  type="number"
                  min={MIN_LINK_DAYS}
                  max={MAX_LINK_DAYS}
                  aria-label="Days a new link works"
                  value={draft.days}
                  disabled={!editable}
                  onChange={(e) => set({ days: e.target.value })}
                  error={daysError}
                />
              </div>
              <SwitchRow
                title="Every link must carry a passcode"
                hint="Off, the person sharing decides. They set it and tell it separately — it never travels in the message."
                checked={draft.passcode}
                disabled={!editable}
                onChange={(passcode) => set({ passcode })}
              />
            </div>
          </SettingsSection>

          {seesShares && (
            <SettingsSection
              id="sending"
              icon={<Send size={18} aria-hidden />}
              title="Sending a link"
              description={`How a link sent by e-mail or WhatsApp leaves ${orgName}.`}
              footer={
                <p className="text-muted-foreground text-xs">
                  The organisation’s own mail server and WhatsApp number are connected in the{' '}
                  <a href={`${consoleUrl.replace(/\/+$/, '')}/integrations`} className="text-primary font-medium hover:underline">
                    FiveOX console
                  </a>
                  . ProDoc holds no credential.
                </p>
              }
            >
              {channels.loading && !channels.data ? (
                <SkeletonRegion label="Asking how a link would be sent">
                  <Skeleton className="h-4 w-64" />
                </SkeletonRegion>
              ) : channels.error ? (
                <p className="text-muted-foreground text-sm">How a link would be sent could not be read: {channels.error}</p>
              ) : (channels.data ?? []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No sending channel is reported for {orgName} yet.</p>
              ) : (
                <ul className="space-y-1.5" aria-label="Sending channels">
                  {(channels.data ?? []).map((c) => {
                    const line = channelLine(c, orgName)
                    return (
                      <li key={c.channel} className="text-sm">
                        <span className="text-foreground font-medium">{FAMILY[c.channel] ?? c.channel}</span> — <span className={line.tone}>{line.text}</span>
                      </li>
                    )
                  })}
                </ul>
              )}
            </SettingsSection>
          )}

          <EditBar dirty={area.dirty} saving={area.saving} onSave={save} onDiscard={area.discard} problem={area.problem} invalid={daysError} editable={editable} />
        </>
      )}
    </EditPage>
  )
}
